import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { isSessionActive } from "@/lib/auth";

const links = [
  { to: "/", label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/#merchants", label: "Merchants" },
  { to: "/#buyers", label: "Buyers" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setSignedIn(isSessionActive());
  }, []);

  return (
    <header className="sticky top-4 z-50 mx-auto w-full px-4 md:px-8">
      <nav
        data-testid="site-navbar"
        className="glass mx-auto flex max-w-6xl items-center justify-between rounded-full px-5 py-3 md:px-7"
      >
        <Link
          to="/"
          data-testid="nav-logo"
          className="group flex items-center gap-2 font-display text-lg font-black tracking-tight"
        >
          <span className="logo-mark inline-block">
            <img src="/logo.png" alt="Checkout DM" className="h-9 w-9 rounded-2xl" />
          </span>
          <span className="transition-transform duration-300 group-hover:translate-x-0.5">
            Checkout DM
          </span>
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight transition-colors hover:text-hotpink ${
                  isActive ? "text-hotpink" : "text-ink/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:block">
          <button
            data-testid="nav-cta-connect"
            onClick={() => navigate(signedIn ? "/dashboard" : "/login")}
            className="cta-glow rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
          >
            {signedIn ? "Go to Dashboard" : "Connect Instagram"}
          </button>
        </div>

        <button
          className="md:hidden"
          onClick={() => setOpen((s) => !s)}
          data-testid="nav-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="glass mx-auto mt-2 max-w-6xl rounded-3xl p-5 md:hidden"
          >
            <div className="flex flex-col gap-3">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="rounded-2xl px-4 py-3 text-base font-medium hover:bg-white/60"
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => {
                  setOpen(false);
                  navigate(signedIn ? "/dashboard" : "/login");
                }}
                className="cta-glow mt-1 rounded-full bg-sunset-gradient px-5 py-3 text-sm font-semibold text-white"
              >
                {signedIn ? "Go to Dashboard" : "Connect Instagram"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
